import { showToast } from '../../../engine/toast';
import { adminSetPassword, adminUpdatePlayer, USER_ROLES, type AdminUser } from '../admin-api';
import { escapeHtml } from '../admin-ui';
import type { PaneCtx } from '../panes/types';
import { checkboxField, errorText, readForm, selectField, submitRow, textField } from './fields';

/**
 * Edit an existing account.
 *
 * TWO independent forms, not one. The profile fields go to the game-owned
 * `PATCH /admin/players/:id`; the password goes to core's
 * `POST /admin/users/:id/password`, which also revokes refresh tokens and
 * pending resets. Keeping them separate means a rejected password (too short,
 * say) cannot discard a profile edit the operator already typed, and an
 * operator who only wanted to fix a typo never touches sessions at all.
 */
export function openEditPlayer(ctx: PaneCtx, user: AdminUser): void {
  ctx.openModal({
    title: 'Edit player',
    subtitle: user.email,
    size: 'lg',
    body: `
      <form id="edit-profile" data-form="edit-profile" class="flex flex-col gap-3">
        ${textField('email', 'Email', user.email, { type: 'email' })}
        ${textField('username', 'Call sign', user.username ?? '')}
        ${selectField('role', 'Role', USER_ROLES, user.role)}
        ${checkboxField('emailVerified', 'Email verified', user.emailVerified)}
      </form>
      ${submitRow('Save profile', 'edit-profile')}

      <div class="mt-4 pt-3 border-t border-border">
        <h3 class="text-[10px] text-text-dim uppercase tracking-wide mb-2">Set password</h3>
        <form id="edit-password" data-form="edit-password" class="flex flex-col gap-3">
          ${textField('password', 'New password', '', {
            type: 'password',
            hint: 'Signs the player out everywhere and voids any pending reset link. You will have to tell them the new password yourself.',
          })}
        </form>
        ${submitRow('Set password', 'edit-password')}
      </div>

      <p class="mt-3 text-[10px] text-text-dim leading-relaxed">
        Player id <code class="text-text-dim">${escapeHtml(user.id)}</code>
      </p>`,

    onSubmit(kind, form, h) {
      const v = readForm(form);

      if (kind === 'edit-profile') {
        // Send only what changed: PATCH rejects an empty body, and resending
        // an unchanged email would trip the unique check against the row itself.
        const body: Record<string, unknown> = {};
        const email = String(v.email ?? '').toLowerCase();
        if (email && email !== user.email.toLowerCase()) body.email = email;
        const username = String(v.username ?? '');
        if (username && username !== (user.username ?? '')) body.username = username;
        if (v.role && v.role !== user.role) body.role = v.role;
        if (Boolean(v.emailVerified) !== user.emailVerified) body.emailVerified = Boolean(v.emailVerified);

        if (Object.keys(body).length === 0) {
          h.setError('Nothing changed.');
          return;
        }
        h.setBusy(true);
        void (async () => {
          try {
            await adminUpdatePlayer(user.id, body);
            showToast('Player updated.', 'success');
            h.close();
            await ctx.reload('players');
          } catch (err) {
            h.setError(errorText(err, 'Could not update that player.'));
            h.setBusy(false);
          }
        })();
        return;
      }

      if (kind === 'edit-password') {
        const password = String(v.password ?? '');
        if (password.length < 8) {
          // Core's SetUserPasswordDto is @Length(8,128) — checked here so the
          // operator gets the rule rather than a raw validation array.
          h.setError('Password must be at least 8 characters.');
          return;
        }
        h.setBusy(true);
        void (async () => {
          try {
            await adminSetPassword(user.id, password);
            showToast('Password set. The player has been signed out everywhere.', 'success');
            h.close();
          } catch (err) {
            h.setError(errorText(err, 'Could not set that password.'));
            h.setBusy(false);
          }
        })();
      }
    },
  });
}
